
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Client } from "@/types/client";
import { useCompanies } from "@/hooks/useCompanies";
import { CompanySelector } from "./ClientDialog/CompanySelector";
import { ClientInfoInputs } from "./ClientDialog/ClientInfoInputs";
import { ClientAssignments } from "./ClientDialog/ClientAssignments";
import { PassportInfoTab } from "./ClientDialog/PassportInfoTab";
import { AdditionalInfoTab } from "./ClientDialog/AdditionalInfoTab";
import { AttachmentsTab } from "./ClientDialog/AttachmentsTab";
import { useCreateClient } from "@/hooks/useCreateClient";
import { useUpdateClient } from "@/hooks/useUpdateClient";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ClientDialogProps {
  client?: Client;
  onSuccess?: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
}

const getInitialFormData = (client?: Client): Omit<Client, "created_at" | "updated_at" | "client_assignments"> => ({
  id: client?.id || "",
  company: client?.company || "",
  company_id: client?.company_id || undefined, // اختياري - سيتم ربطه بترافليون تلقائياً
  name_ar: client?.name_ar || "",
  name_en: client?.name_en || "",
  mobile: client?.mobile || "",
  additional_info: client?.additional_info || "",
  passport_info: client?.passport_info || null,
  attachments: client?.attachments || [],
  status: client?.status || "waiting",
  created_by: client?.created_by || null
});

export const ClientDialog = ({ client, onSuccess, open, onOpenChange, trigger }: ClientDialogProps) => {
  const [formData, setFormData] = useState(getInitialFormData(client));
  const [activeTab, setActiveTab] = useState("basic");
  const { data: companies = [], refetch: refetchCompanies } = useCompanies();
  const queryClient = useQueryClient();

  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();

  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData(client));
      setActiveTab("basic");
    }
  }, [client, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (client) {
        await updateClientMutation.mutateAsync({ id: client.id, ...formData });
        toast.success("تم تحديث العميل بنجاح");
      } else {
        await createClientMutation.mutateAsync(formData);
        toast.success("تم إضافة العميل بنجاح");
      }
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(error.message || (client ? "حدث خطأ أثناء تحديث العميل" : "حدث خطأ أثناء إضافة العميل"));
      console.error("Error saving client:", error);
    }
  };
  
  const handleFormChange = (data: Partial<Omit<Client, "id" | "created_at" | "updated_at" | "client_assignments">>) => {
    setFormData(prev => ({ ...prev, ...data }));
  };

  const handleCompanyAdded = (companyId: string, companyName: string) => {
    refetchCompanies().then((queryResult) => {
      const allCompanies = queryResult.data || companies;
      const company = allCompanies.find(c => c.id === companyId);
      setFormData(prev => ({ 
        ...prev, 
        company_id: companyId,
        company: company?.name_ar || companyName
      }));
    });
  };

  const handleCompanyChange = (companyId: string) => {
    const selectedCompany = companies.find(c => c.id === companyId);
    setFormData(prev => ({ 
      ...prev, 
      company_id: companyId,
      company: selectedCompany?.name_ar || ""
    }));
  };

  const isSaving = createClientMutation.isPending || updateClientMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>{client ? "تعديل عميل" : "إضافة عميل جديد"}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">المعلومات الأساسية</TabsTrigger>
              <TabsTrigger value="additional">معلومات إضافية</TabsTrigger>
              <TabsTrigger value="passport">معلومات جواز السفر</TabsTrigger>
              <TabsTrigger value="attachments">المرفقات</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <CompanySelector
                companyId={formData.company_id || ''}
                onCompanyChange={handleCompanyChange}
                onCompanyAdded={handleCompanyAdded}
                companies={companies}
              />
              
              <ClientInfoInputs 
                formData={formData}
                onFormChange={handleFormChange}
              />

              {client && (
                <ClientAssignments client={client} />
              )}
            </TabsContent>

            <TabsContent value="additional">
              <AdditionalInfoTab
                formData={formData}
                onFormChange={handleFormChange}
              />
            </TabsContent>

            <TabsContent value="passport">
              <PassportInfoTab
                formData={formData}
                onFormChange={handleFormChange}
              />
            </TabsContent>

            <TabsContent value="attachments">
              <AttachmentsTab
                formData={formData}
                onFormChange={handleFormChange}
              />
            </TabsContent>
          </Tabs>

          {!client && (
            <p className="mt-4 text-sm text-gray-500 text-center" dir="rtl">
              سيتم تسجيلك كمنشئ لهذا العميل وتخصيصك تلقائياً كموظف مبيعات.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              إلغاء
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={isSaving}>
              {isSaving ? "جاري الحفظ..." : (client ? "تحديث" : "إضافة")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
